import { createSlice } from '@reduxjs/toolkit';

const initialState = {
	name: 'Untitled Template',
	variant: 'design',
	json: {
		width: 1080,
		height: 1080,
		fonts: [],
		pages: [{ id: 'page_1', children: [], width: 'auto', height: 'auto', background: 'white', bleed: 0 }],
		unit: 'px',
		dpi: 96
	},
	status: 'draft',
	history: [],
	tags$Category: [],
	tags$General: [],
	etc: {
		previewVideo: null, // This will hold the URL of the preview video if it exists
		directives: {},
		cfg: {
			global: {
				minPages: 1,
				maxPages: 10
			},
			customer: {
				minPages: 1,
				maxPages: 10
			},
			order: {
				countries: null,
				itemId: null,
			},
			canBeResized: true,
		},
	}
};

const ALLOWED_TEMPLATE_KEYS = new Set([
	'_id',
	'creator',
	'dtIns',
	'dtUpd',
	'jsonContext',
	'etc',
	'history',
	'json',
	'name',
	'owner',
	'rasters',
	'returnPath',
	'source',
	'status',
	'tags$Category',
	'tags$General',
	'typeNameFq',
	'user',
	'variant',
]);

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

export const sanitizeTemplateState = (candidateState = {}) => {
	const nextState = { ...initialState };

	Object.keys(candidateState).forEach((key) => {
		if (ALLOWED_TEMPLATE_KEYS.has(key) && candidateState[key] !== undefined) {
			nextState[key] = candidateState[key];
		}
	});

	nextState.history = Array.isArray(nextState.history) ? nextState.history : [];
	nextState.tags$Category = Array.isArray(nextState.tags$Category) ? nextState.tags$Category : [];
	nextState.tags$General = Array.isArray(nextState.tags$General) ? nextState.tags$General : [];

	// `etc` is the canonical home for cfg + directives. Start from whatever `etc`
	// the candidate carried (new shape), falling back to the initial etc.
	const candidateEtc = isPlainObject(candidateState.etc) ? candidateState.etc : {};
	const nextEtc = isPlainObject(nextState.etc)
		? { ...initialState.etc, ...nextState.etc }
		: { ...initialState.etc };

	// Fold any legacy root-level cfg / directives into etc. The new shape
	// (etc.cfg / etc.directives) wins; root values only fill gaps where etc
	// lacks them. This runs independent of the allowed-keys filter so legacy
	// root values are still captured even though cfg/directives are no longer
	// allowed at the root.
	if (candidateEtc.cfg !== undefined) {
		nextEtc.cfg = candidateEtc.cfg;
	} else if (candidateState.cfg !== undefined) {
		nextEtc.cfg = candidateState.cfg;
	}

	if (candidateEtc.directives !== undefined) {
		nextEtc.directives = candidateEtc.directives;
	} else if (candidateState.directives !== undefined) {
		nextEtc.directives = candidateState.directives;
	}

	nextState.etc = nextEtc;

	// Root cfg / directives must never survive on the sanitized state.
	delete nextState.cfg;
	delete nextState.directives;

	nextState.source = isPlainObject(nextState.source) ? nextState.source : undefined;

	return nextState;
};

export const templateSlice = createSlice({
	name: 'template',
	initialState,
	reducers: {
		setTemplateState: (state, action) => {
			//null is not a valid
			if (!action.payload) {
				return initialState;
			} else {
				return sanitizeTemplateState({ ...state, ...action.payload });
			}
		},
		resetTemplateState: () => initialState
	}
});

// Action creators are generated for each case reducer function
export const { setTemplateState, resetTemplateState } = templateSlice.actions;

export const getTemplateState = (state) => state.template;
export const getDefaultTemplateState = (state) => initialState;

export default templateSlice.reducer;
